/* eslint-disable import/prefer-default-export,object-curly-newline */
/* global G */

import { isStr } from 'lib/util'

/**
 * Inserts `message` into `data` according to the current sort order.
 * In either way, the mutation is always in place, changing `data`.
 *
 * @param {Message[]} data
 * @param {Message} message
 */
export const insertMessage = (data, message) => {
  const [a, b] = data
  // is it ascending?
  a?.value?.submitTimestamp <= b?.value?.submitTimestamp
    ? data.push(message)
    : data.splice(0, 0, message)
}

/**
 * Sorts messages using oldest to newest.
 *
 * @param {Message[]} messages
 * @returns {Message[]}
 */
export const sortMessages = messages => [...messages].sort(
  (a, b) => {
    const ta = a?.value?.submitTimestamp
    const tb = b?.value?.submitTimestamp
    if (ta && tb) return (ta > tb) - (ta < tb)
    if (ta === tb) return 0
    
    return ta ? -1 : 1
  },
)

/**
 * Merges new and old messages.
 *
 * @param {Message[]} existing
 * @param {Message[]} incoming
 * @returns {Message[]}
 */
export const mergeMessages = (existing, incoming) => {
  const byKey = new Map(existing.map(m => [m.key, m]))
  incoming.forEach((m) => { byKey.set(m.key, m) })

  return sortMessages([...byKey.values()])
}

/**
 * Returns the parts of the URL to be used to generate the messages URL. For that, it makes use
 * of the current model's api values, that can be overridden with the optional `api` object.
 *
 * @param {Gaia.Web.Application} app    the Platform Web Application
 * @param {Object} [props]              an optional object, in case we want to override the current
 *                                      model values to form the URL
 * @returns {{ref, host, api, version}} the URL parts
 */
const getUrlParts = (app, props = {}) => {
  const appState = app[G.SESSION][G.STATE]
  const host = appState[G.HTTP]
  const model = appState[G.MODULE][G.MODEL]
  const version = props.version || model[G.PROPS].version
  const urlParts = { host, version, ...props.key && { key: props.key } }
  const part = Object.keys(urlParts).find(i => !urlParts[i])
  // eslint-disable-next-line no-unused-expressions
  if (part) {
    throw ReferenceError(`${part} is required`)
  }
  return urlParts
}

/**
 * Receives an object of resolved refs and returns and object only containing their keys.
 *
 * @param {Object} refs
 * @returns {Object}
 */
export const getRefKeys = refs => Object.keys(refs).reduce((acc, key) => {
  acc[key] = refs[key].map(r => (isStr(r) ? r : r.key))
  return acc
}, {})

/**
 * Returns the messages url.
 *
 * @param {Gaia.Web.Application} app    the Platform Web Application
 * @param {Object} [props]
 * @returns {string}
 */
export const getUrl = (app, props) => {
  const { host, version, key = '' } = getUrlParts(app, props)
  return `${host}/api/v${version}/message/${key}`
}

/**
 * Disables the loader middleware
 *
 * @param {Function} loader   loader middleware
 * @param {object} middleware other middlewares
 * @returns {unknown[]}
 */
export const noLoaderMiddleware = ({ loader, ...middleware }) => Object.values(middleware)
